/**
 * @file Name the file type of links that point at documents.
 *
 * A PDF or an Office file is not a web page: following one hands the visitor
 * off to the browser's document viewer or to another application, where the
 * site's navigation is gone and the back button is the only way out. Screen
 * reader users meet a different document mode with no warning. So the link
 * says what it opens, and opens it beside the page rather than over it — the
 * same bargain external links already make here, for the same reason
 * (WCAG 3.2.5).
 *
 * Done in the browser, and for the same reason external-links.js is: a link
 * reaches a page from body text, link fields on paragraphs and cards, menus
 * and the footer, and catching them server-side would need a hook in each.
 * A visitor without JS follows an ordinary link to an ordinary file.
 *
 * This module only names the file type. Sending the link to a new tab is
 * external-links.js's job: "leaves the page" is one rule with two grounds —
 * off-site, or a document — and splitting the decision across two modules
 * meant neither could be read on its own. It imports isDocumentUrl() from
 * here so the list of extensions has one home.
 *
 * Runs immediately before externalLinks() in main.js, which is now only about
 * how the two notices read: "Handbook (PDF) (opens in a new tab)".
 */

/* global Drupal, once */

import { fileExtensionFromUrl } from "./_helpers.js";

/**
 * The file types announced, by extension.
 *
 * Only the formats that take the visitor out of the browsing context are
 * listed. A `.txt` or `.csv` renders as text in the tab it was opened from and
 * needs no warning; the Office formats and PDF do not.
 *
 * `label` is a function so Drupal.t() resolves at the point of use rather than
 * whenever this module happens to be imported, which may be before Drupal is
 * on the page. `announced` matches a name that already says the type — an
 * editor who wrote "(PDF)" into the link text, or a file field printing a name
 * ending ".docx" — so it is not said twice.
 *
 * It matches the extension or the whole label, never the product name alone:
 * "word", "excel" and "presentation" are ordinary English, and "A word from
 * our president.docx" is a link that needs the notice rather than one that
 * already carries it.
 */
const DOCUMENT_TYPES = {
  pdf: { label: () => Drupal.t("PDF"), announced: /\bpdf\b/i },
  doc: {
    label: () => Drupal.t("Word document"),
    announced: /\bdocx?\b|\bword document\b/i,
  },
  xls: {
    label: () => Drupal.t("Excel spreadsheet"),
    announced: /\bxlsx?\b|\bexcel spreadsheet\b/i,
  },
  ppt: {
    label: () => Drupal.t("PowerPoint presentation"),
    announced: /\bpptx?\b|\bpowerpoint presentation\b/i,
  },
};

/** Maps each recognised extension onto its entry in DOCUMENT_TYPES. */
const EXTENSIONS = {
  pdf: "pdf",
  doc: "doc",
  docx: "doc",
  xls: "xls",
  xlsx: "xls",
  ppt: "ppt",
  pptx: "ppt",
};

/**
 * Returns the document type a link points at, or null for anything else.
 *
 * @param {string} url - An absolute or relative URL.
 * @returns {{label: function, announced: RegExp}|null}
 */
function documentType(url) {
  const extension = fileExtensionFromUrl(url);
  return extension ? (DOCUMENT_TYPES[EXTENSIONS[extension]] ?? null) : null;
}

/**
 * Returns whether an address points at a document rather than a page.
 *
 * Exported for external-links.js, which sends these to a new tab alongside the
 * off-site ones.
 *
 * @param {string} url - An absolute or relative URL.
 * @returns {boolean}
 */
export function isDocumentUrl(url) {
  return documentType(url) !== null;
}

/**
 * Adds the file-type notice to a link's accessible name.
 *
 * @param {HTMLAnchorElement} link - The link to annotate.
 * @param {string} label - The type to announce, e.g. "PDF".
 */
function announce(link, label) {
  const ariaLabel = link.getAttribute("aria-label");

  /* An aria-label replaces the link's contents for assistive technology, so on
     a labelled link the notice has to join the label — appended as text it
     would never be read. Same handling as external-links.js. */
  if (ariaLabel !== null) {
    link.setAttribute("aria-label", `${ariaLabel}, ${label}`);
    return;
  }

  const notice = document.createElement("span");
  /* `visually-hidden` (Drupal core's system/base, on every page) rather than
     the `sr-only` the twig templates use: Tailwind generates a utility only
     where it can see the literal class name, and its content globs do not
     reach this directory. */
  notice.className = "visually-hidden";
  notice.textContent = ` (${label})`;
  link.appendChild(notice);
}

/**
 * Annotates the document links within a context.
 *
 * Idempotent: elements are collected with once(), so repeat attaches — and the
 * notice — happen a single time per link.
 *
 * @param {HTMLElement|Document} context
 *   The scope to search (document on full page load, the inserted wrapper on
 *   Drupal AJAX updates).
 */
export function documentLinks(context) {
  once("ts-cswe-document-link", "a[href]", context).forEach((link) => {
    const type = documentType(link.href);
    if (!type) {
      return;
    }

    if (
      !type.announced.test(link.getAttribute("aria-label") ?? link.textContent)
    ) {
      announce(link, type.label());
    }
  });
}
